import { ExecutorStatus, isAWSAccount, isAzureAccount, isGCPAccount } from "types/account";
import { CustomerClient } from "client/base";
import { delay } from "utils/promise";
import { getAPIToken } from "utils/request";
import { baseRequestURL, redirectIfArmenTesting } from "api/BaseAPI";
import { sendPresignedRequest } from "@/utils/cloud/presigned_url";
const PREFIX = "account/";
const tokenToClient = (token, account) => {
    const headers = getHeaders(token.headers, account);
    const url = stripUrl(getUrl(token.base_url, account));
    return new CustomerClient(url, headers, account);
};
const getSTSClientErrorHandler = (token) => {
    return (e) => {
        console.log("error in sts client request token:");
        console.log(token);
        console.log(`current epoch: ${Math.floor(Date.now() / 1000)}`);
        throw e;
    };
};
const isValidToken = (token) => {
    // add a good buffer assume tokens only last 5 minutes
    const currentEpoch = Math.floor(Date.now() / 1000) + 55 * 60;
    return currentEpoch < token.expires_on_epoch;
};
class STSBackedClient extends CustomerClient {
    cache;
    getToken;
    constructor(cache, getToken, account) {
        super(cache.client.baseUrl, {}, account);
        this.cache = cache;
        this.getToken = getToken;
    }
    checkAndParse(response, noLogout, noErrorToast, isLogoutCheck) {
        return (this.cache.client
            .checkAndParse(response, noLogout, noErrorToast, isLogoutCheck)
            // Add an error handler for functions that parse the response.
            .catch(getSTSClientErrorHandler(this.cache.token)));
    }
    _getCacheIfValid() {
        const { token, client } = this.cache;
        if (isValidToken(token)) {
            return { token, client };
        }
        return undefined;
    }
    _request(url, init, timeout = 1000) {
        const cache = this._getCacheIfValid();
        if (cache !== undefined) {
            return cache.client._request(url, init, timeout);
        }
        console.log("fetching new sts token");
        return this.getToken().then((token) => {
            const client = tokenToClient(token, this.account);
            const currentCache = this._getCacheIfValid();
            if (isValidToken(token)) {
                if (currentCache === undefined) {
                    console.log("updating cached sts token");
                    this.cache = { token, client };
                }
            }
            else if (currentCache !== undefined) {
                return currentCache.client._request(url, init, timeout);
            }
            return client._request(url, init, timeout);
        });
    }
    static create(getToken, account) {
        return getToken().then((token) => new STSBackedClient({ token, client: tokenToClient(token, account) }, getToken, account));
    }
}
function getHeaders(headers, account) {
    if (getIsRelayAccount(account)) {
        return [["Authorization", "Bearer " + getAPIToken()], ...headers];
    }
    return headers;
}
function getIsRelayAccount(account) {
    const relayMode = account?.configuration?.features?.includes("communication-relay") ?? false;
    return relayMode && account?.id !== undefined;
}
function getCommunicateBaseUrl(account) {
    return baseRequestURL + `v1/account/${account?.id}/communicate`;
}
/** Get the base URL for a customer client */
function getUrl(url, account) {
    const armenUrl = redirectIfArmenTesting(url, account);
    if (armenUrl) {
        return armenUrl;
    }
    if (getIsRelayAccount(account)) {
        return getCommunicateBaseUrl(account);
    }
    return url;
}
function stripUrl(url) {
    if (url.endsWith("/scores")) {
        url = url.slice(0, -6);
    }
    if (url.endsWith("/v1/")) {
        url = url.slice(0, -3);
    }
    if (!url.endsWith("/")) {
        url = url + "/";
    }
    return url;
}
export function getAgentEndpoint(account) {
    if (account.configuration.agent_base_url !== undefined) {
        return account.configuration.agent_base_url;
    }
    else if (account.configuration.cloud_base_url !== undefined) {
        return account.configuration.cloud_base_url.replace("aible-app", "aible-agent");
    }
    throw Error("no configured agent endpoint");
}
export const OPENSHELL_BASE_URL = "http://127.0.0.1:9000";
export function getOpenShellEndpoint(account) {
    if (account.configuration.openshell_base_url !== undefined) {
        return account.configuration.openshell_base_url;
    }
    return OPENSHELL_BASE_URL;
}
export function getClient(url, headers, account) {
    return new CustomerClient(stripUrl(url), headers, account);
}
/** An AccountClient that can be used to implement CloudClient functionality */
export class AccountClientWithoutFactory {
    base;
    constructor(base) {
        this.base = base;
    }
    getAccount(id) {
        return this.base.get(PREFIX + id);
    }
    getPresignedUrl(id, name) {
        return this.base.post(`${PREFIX}${id}/presigned_url`, {
            file_name: name
        });
    }
    getAccountExecutor(account) {
        const { executor } = account.configuration;
        if (executor !== undefined && executor.status === ExecutorStatus.Running) {
            return Promise.resolve(executor);
        }
        return this.getExecutor(account.id);
    }
    getAppConfig() {
        return this.base.get("config");
    }
    async getExecutor(id) {
        const executor = await this.base.post(`${PREFIX}${id}/executor`);
        switch (executor.status) {
            case ExecutorStatus.Pending:
                return delay(2000).then(() => this.getExecutor(id));
            case ExecutorStatus.Error:
                throw Error("error creating executor");
            case ExecutorStatus.Running:
                return executor;
        }
    }
    getSTSToken(id) {
        return this.base.get(`${PREFIX}${id}/foreign_sts`);
    }
    /** Construct a client for making a customer cloud request */
    getAccountClient(account) {
        if (isAWSAccount(account)) {
            let headers = undefined;
            const credentials = "include";
            headers = { credentials: credentials };
            return Promise.resolve(getClient(getUrl(account.configuration.lambda_api_endpoint, account), headers, account));
        }
        if (isGCPAccount(account) || isAzureAccount(account)) {
            return STSBackedClient.create(() => this.getSTSToken(account.id), account);
        }
        return this.getAccountExecutor(account).then((e) => {
            let headers = e.endpoint_headers;
            if (getIsRelayAccount(account)) {
                const endpoint_headers = new Headers(e.endpoint_headers);
                const headersArray = [];
                for (const hPair of endpoint_headers.entries()) {
                    headersArray.push([hPair[0], hPair[1]]);
                }
                headers = getHeaders(headersArray, account);
            }
            return getClient(getUrl(e.endpoint_url, account), headers, account);
        });
    }
    /** Get the base URL for cloud requests for an account. */
    async getCloudBaseUrl(account) {
        // The API doesn't actually type it's configuration, but does generally
        // follow a pattern based on the type. We want to follow the logic the api
        // is using for determining a base url of an account.
        // We have to do a little bit of wrangling to make sure we are talking about
        // the prefix to match for.
        let cloud_base_url = account.configuration?.cloud_base_url;
        // AWS accounts use the lambda_api_endpoint, but it is not the first thing
        // checked on the api.
        if (cloud_base_url === undefined && account.type == "AWS") {
            cloud_base_url = account.configuration.lambda_api_endpoint;
        }
        // Next the executor url is used. If this isn't defined there is likely
        // something very wrong with how the account is configured.
        if (cloud_base_url === undefined) {
            const executor = await this.getAccountExecutor(account);
            cloud_base_url = executor.endpoint_url;
        }
        // Unlikely, but better to know that we have accounts without base urls
        // which should not be the case.
        if (cloud_base_url === undefined) {
            throw new Error(`Account (${account.id}) does not have a base url.`);
        }
        if (typeof cloud_base_url !== "string") {
            throw new Error(`Account base url was determined to be something other than a string: ${typeof cloud_base_url}`);
        }
        return cloud_base_url;
    }
    async handlePresignedUrl(method, account, url, options) {
        const data = options?.data;
        const filename = options?.filename || new Date().toISOString();
        const useCommunicationRelay = getIsRelayAccount(account);
        const cloud_base_url = await this.getCloudBaseUrl(account);
        // If this is a request to our cloud infrastructure to handle a locally
        // hosted file, we need to make a request with the appropriate headers.
        if (url.startsWith(cloud_base_url)) {
            const client = this.base;
            const url_path = url.substring(cloud_base_url.length);
            // Accounts that use the communication relay, have to be proxied through
            // the api.
            if (useCommunicationRelay) {
                // MB: We are using the base client which already handles the base_url
                // for talking to the api. So we are translating the presigned_url
                // request strictly to use the communication relay.
                const requestUrl = `account/${account?.id}/communicate` + url_path;
                const clientRequestInit = client._init(method, data);
                const request = () => client.request(requestUrl, clientRequestInit);
                return this.constructPresignedResponse(method, {
                    request,
                    download: () => this.downloadInternalPresignedFile(filename, request)
                });
            }
            // Constructing a request to our cloud infrastructure requires us to have
            // a STSBackedClient as we send the tokens that are generated as a part of
            // the request. These tokens are expected on any request.
            const accountClient = (await this.getAccountClient(account));
            const accountClientInitRequest = accountClient._init(method, data);
            const request = () => accountClient.request(url, accountClientInitRequest);
            return this.constructPresignedResponse(method, {
                request,
                download: () => this.downloadInternalPresignedFile(filename, request)
            });
        }
        // Requests to cloud providers are much simpler and don't require much handling.
        if (method === "GET") {
            const request = () => fetch(url);
            const ResponseAs = {
                type: "PresignedGetResponseAs",
                getResponse: request,
                downloadFile: async () => {
                    window.open(url, "_blank");
                }
            };
            return ResponseAs;
        }
        if (method === "PUT" && data !== undefined) {
            // Already existing functionality for handling presigned urls from armen.
            return await sendPresignedRequest(data, url);
        }
        throw new Error(`Encountered an error while attempting to handle a presigned url.`);
    }
    async constructPresignedResponse(method, handlers) {
        if (method == "GET") {
            const ret = {
                type: "PresignedGetResponseAs",
                getResponse: handlers.request,
                downloadFile: handlers.download
            };
            return ret;
        }
        return await handlers.request();
    }
    async downloadInternalPresignedFile(filename, request) {
        const response = await request();
        if (!response.ok) {
            throw new Error("Failed to get an OK from a presigned url request");
        }
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
    }
    /** Utility wrapper for downloading a file through a presigned url */
    async presignedGetRequest(account, url, name) {
        return await this.handlePresignedUrl("GET", account, url, { filename: name });
    }
    /** Utility wrapper for uploading a file through a presigned url */
    async presignedPutRequest(account, url, data) {
        return await this.handlePresignedUrl("PUT", account, url, { data });
    }
}
